import React, { useEffect, useState, useRef } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, ScrollView, FlatList, TouchableOpacity, Image, Modal, TextInput, Button, Alert, RefreshControl, DeviceEventEmitter } from 'react-native';
import { fetchCommunityById, fetchCommunityTopPosts, fetchCommunityPosts, fetchUserCommunities, updateCommunity, getUserCommunityRole, getCommunityMembers, getCommunityMembersWithFunction, joinCommunity } from '../../services/communityService';
import { useAuth } from '../../Utils/AuthProvider';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import PostCard from '../../Components/PostCard';
import { getUserData } from '../../services/userService';
import defaultCommunityImg from '../../../assets/images/defaultuser.png';
import { uploadFile, getSupabaseFileUrl } from '../../services/imageService';
import { launchImageLibrary } from 'react-native-image-picker';
import { PermissionManager } from '../../Utils/permissions';
import { theme } from '../../constants/theme';
import { validateFileSize, showFileSizeError, showFileSizeInfo } from '../../Utils/fileValidation';
import { Supabase } from '../../Utils/supabase.config';
import ScreenWrapper from '../../Components/ScreenWrapper';
import Header from '../../Components/Header';
import FullScreenMedia from '../../Components/FullScreenMedia';
import Avatar from '../../Components/Avatar';
import { hp, wp } from '../../helpers/common';

const tabs = [
  { key: 'about', label: 'About' },
  { key: 'feed', label: 'Posts' },
  { key: 'members', label: 'Members' },
];

const AboutTab = ({ communityId, profileImage }: { communityId: string; profileImage: string }) => {
  const { user } = useAuth();
  const [community, setCommunity] = useState<any>(null);
  const [creator, setCreator] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editModal, setEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editImage, setEditImage] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [fullScreenVisible, setFullScreenVisible] = useState(false);


  useEffect(() => {
    const loadCommunityData = async () => {
      setLoading(true);
      const res = await fetchCommunityById(communityId);
      if (res.success && res.data) {
        setCommunity(res.data);
        setEditName(res.data.name);
        setEditDesc(res.data.description);
        setEditImage(res.data.profile_image || null);
        // Fetch creator info
        if (res.data?.created_by) {
          const userRes = await getUserData(res.data.created_by);
          if (userRes.success) setCreator(userRes.data);
        }
      } else setError('Failed to load community info');
      setLoading(false);
    };

    const loadUserRole = async () => {
      if (user) {
        const roleRes = await getUserCommunityRole(user.id, communityId);
        if (roleRes.success) {
          setUserRole(roleRes.data);
        }
      }
    };

    loadCommunityData();
    loadUserRole();
  }, [communityId, user]);

  const handlePickEditImage = async () => {
    // Request media permissions before launching picker
    const permissionResult = await PermissionManager.requestMediaPermissions();
    if (!permissionResult.granted) {
      Alert.alert('Permission Required', 'Gallery access is required to select an image.');
      return;
    }

    const result = await launchImageLibrary({ mediaType: 'photo', quality: 0.7 });
    if (result.assets && result.assets.length > 0) {
      const asset = result.assets[0];
      if (asset.uri) {
        // Validate file size before proceeding
        const sizeValidation = await validateFileSize(asset.uri);
        if (!sizeValidation.isValid) {
          showFileSizeError(sizeValidation.errorMessage || 'File size validation failed');
          return;
        }

        // Show file size info in console
        showFileSizeInfo(sizeValidation.size);

        setEditImage(asset.uri);
      }
    }
  };

  const handleUpdateCommunity = async () => {
    setUpdating(true);
    let imagePath = community.profile_image;
    if (editImage && editImage !== community.profile_image) {
      const uploadResult = await uploadFile('uploads', editImage, true, null, 'community-profiles');
      imagePath = uploadResult && uploadResult.success ? uploadResult.data : community.profile_image;
    }
    const res = await updateCommunity({
      id: community.id,
      name: editName,
      description: editDesc,
      profile_image: imagePath,
      is_announcement_only: community.is_announcement_only,
    });
    setUpdating(false);
    if (res.success) {
      setCommunity(res.data);
      setEditModal(false);
    } else {
      Alert.alert('Error', res.error?.message || 'Failed to update community');
    }
  };

  const handleJoin = async () => {
    try {
      setJoining(true);
      if (!user) {
        Alert.alert('Error', 'You must be logged in to join this community');
        return;
      }
      const res = await joinCommunity(user.id, communityId);
      if (res.success) {
        // Refresh role to reflect membership
        const roleRes = await getUserCommunityRole(user.id, communityId);
        if (roleRes.success) setUserRole(roleRes.data);
      } else {
        Alert.alert('Error', res.error?.message || 'Failed to join community');
      }
    } finally {
      setJoining(false);
    }
  };

  const handleImagePress = () => {
    // Only open full screen if there's an image to show
    if (community?.profile_image || profileImage) {
      setFullScreenVisible(true);
    }
  };

  const getImageUrlForFullScreen = () => {
    // FullScreenMedia expects a path string, not a full URL
    // It will call getSupabaseFileUrl internally
    if (community?.profile_image) {
      return community.profile_image;
    }
    // If profileImage is a local URI (from image picker), return it as-is
    // FullScreenMedia should handle it, but if it's a full URL, we might need to extract the path
    // For now, return profileImage if it exists
    return profileImage || '';
  };

  const closeFullScreen = () => {
    setFullScreenVisible(false);
  };

  if (loading) return <ActivityIndicator style={{ marginTop: 32 }} />;
  if (error) return <Text style={styles.error}>{error}</Text>;
  if (!community) return <Text style={styles.error}>Community not found</Text>;

  const isCreator = user && community.created_by === user.id;
  const isAdmin = userRole === 'admin' || userRole === 'moderator';
  const isOfficialAnnouncement = communityId === '8ecbadd1-e1c0-4d31-98c4-e58527167323';

  return (
    <ScrollView contentContainerStyle={{ padding: 24 }}>
      <TouchableOpacity onPress={handleImagePress} activeOpacity={0.8}>
        <Image
          source={community.profile_image ? getSupabaseFileUrl(community.profile_image) : profileImage ? { uri: profileImage } : defaultCommunityImg}
          style={styles.detailsCommunityAvatar as any}
        />
      </TouchableOpacity>
      
      <View style={styles.adminActions}>
        {isCreator && (
          <TouchableOpacity style={styles.editButton} onPress={() => setEditModal(true)}>
            <Text style={styles.editButtonText}>Edit</Text>
          </TouchableOpacity>
        )}
        {/* Join button for non-members (disabled for Official Announcements) */}
        {!isCreator && !isAdmin && !userRole && !isOfficialAnnouncement && (
          <TouchableOpacity style={styles.editButton} onPress={handleJoin} disabled={joining}>
            <Text style={styles.editButtonText}>{joining ? 'Joining...' : 'Join'}</Text>
          </TouchableOpacity>
        )}

      </View>

      <Text style={styles.aboutTitle}>{community.name}</Text>
      <Text style={styles.aboutDesc}>{community.description}</Text>
      
      {community.is_announcement_only && (
        <View style={styles.announcementBadge}>
          <Text style={styles.announcementBadgeText}>📢 Announcement Only</Text>
        </View>
      )}
      
      <Text style={styles.aboutMeta}>Created by: {creator?.name || community.created_by}</Text>
      <Text style={styles.aboutMeta}>
        Created at: {community.created_at ? new Date(community.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
      </Text>
      
      {userRole && (
        <Text style={styles.aboutMeta}>Your role: {userRole}</Text>
      )}

      {/* Edit Modal */}
      <Modal
        visible={editModal}
        transparent
        animationType="fade"
        onRequestClose={() => setEditModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Edit Community</Text>
            <TextInput
              style={styles.input}
              value={editName}
              onChangeText={setEditName}
              placeholder="Community name"
            />
            <TextInput
              style={styles.input}
              value={editDesc}
              onChangeText={setEditDesc}
              placeholder="Description"
              multiline
            />
            <TouchableOpacity style={styles.imagePicker} onPress={handlePickEditImage}>
              <Text style={styles.imagePickerText}>Pick Image</Text>
            </TouchableOpacity>
            {editImage && (
              <Image source={{ uri: editImage }} style={styles.imagePreview as any} />
            )}
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <Button title="Cancel" onPress={() => setEditModal(false)} />
              <Button title="Update" onPress={handleUpdateCommunity} disabled={updating} />
            </View>
          </View>
        </View>
      </Modal>

      {/* Full Screen Media Modal */}
      <FullScreenMedia
        visible={fullScreenVisible}
        onClose={closeFullScreen}
        mediaUrl={getImageUrlForFullScreen()}
        mediaType="image"
        title={community?.name}
      />

    </ScrollView>
  );
};

const PostsTab = ({ communityId }: { communityId: string }) => {
  const { user } = useAuth();
  const navigation = useNavigation();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [authorAdminMap, setAuthorAdminMap] = useState<Record<string, boolean>>({});
  const [isLinkAllowed, setIsLinkAllowed] = useState(false);
  const PostCardAny = PostCard as any;

  useEffect(() => {
    loadPosts();
    checkUserMembership();
  }, [communityId]);

  // Listen for new post creation event
  useEffect(() => {
    const newPostListener = DeviceEventEmitter.addListener('newPostCreated', async (newPostData: any) => {
      // Check if the new post belongs to this community
      if (newPostData && newPostData.id && newPostData.community_id === communityId) {
        // Fetch user data for the new post
        const userRes = await getUserData(newPostData.userId);
        const postWithUser = {
          ...newPostData,
          user: userRes.success && userRes.data ? {
            id: userRes.data.id || '',
            name: userRes.data.name || '',
            image: userRes.data.image
          } : undefined,
          postLikes: [],
          comments: [{count: 0}]
        };
        
        // Add to the beginning of the posts list
        setPosts(prevPosts => {
          // Filter out any existing post with the same ID first, then add the new one
          const filtered = prevPosts.filter(p => p.id !== postWithUser.id);
          return [postWithUser, ...filtered];
        });

        // Batch-check admin status for the new post author (deduped/cached)
        if (newPostData?.userId) {
          await ensureAdminStatusForAuthors([String(newPostData.userId)]);
        }
      }
    });

    return () => {
      newPostListener.remove();
    };
  }, [communityId]);

  // Refresh posts when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      loadPosts();
    }, [communityId])
  );

  const checkUserMembership = async () => {
    if (user?.id) {
      const result = await getUserCommunityRole(user.id, communityId);
      if (result.success && result.data) {
        setUserRole(result.data);
        setIsMember(true);
      } else {
        setUserRole(null);
        setIsMember(false);
      }
    }
  };

  const ensureAdminStatusForAuthors = async (authorIds: string[]) => {
    const uniqueAuthorIds = Array.from(new Set(authorIds.filter(Boolean)));
    if (uniqueAuthorIds.length === 0) return;

    // Only check authors we haven't checked yet
    const toCheck = uniqueAuthorIds.filter(id => authorAdminMap[id] === undefined);
    if (toCheck.length === 0) return;

    try {
      const results = await Promise.all(
        toCheck.map(async (authorId) => {
          const { data, error } = await Supabase.rpc('check_user_community_admin', {
            user_uuid: authorId,
            community_uuid: communityId,
          });
          if (error) {
            return { authorId, isAdmin: false };
          }
          return { authorId, isAdmin: Boolean(data) };
        })
      );

      setAuthorAdminMap(prev => {
        const next = { ...prev };
        results.forEach(r => {
          next[r.authorId] = r.isAdmin;
        });
        return next;
      });
    } catch (e) {
      // If the RPC fails, default to "not admin" so links remain plain text
    }
  };

  const loadPosts = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const res = await fetchCommunityPosts(communityId, 15, isLoadMore ? page * 15 : 0);
      if (res.success && res.data) {
        // Determine link settings (prefer server-provided community metadata if present)
        const firstCommunity = res.data?.[0]?.community;
        if (firstCommunity && typeof (firstCommunity as any)?.is_linkallowed === 'boolean') {
          setIsLinkAllowed(Boolean((firstCommunity as any).is_linkallowed));
        } else {
          // Fallback: fetch community info once if not included
          const communityRes = await fetchCommunityById(communityId);
          if (communityRes.success && typeof (communityRes.data as any)?.is_linkallowed === 'boolean') {
            setIsLinkAllowed(Boolean((communityRes.data as any).is_linkallowed));
          } else {
            setIsLinkAllowed(false);
          }
        }

        // Batch-check admin status for authors in this page
        const authorIds = (res.data || [])
          .map((p: any) => String(p?.userId || p?.user_id || p?.user?.id || ''))
          .filter(Boolean);
        await ensureAdminStatusForAuthors(authorIds);

        if (isLoadMore) {
          setPosts(prev => [...prev, ...res.data]);
          setPage(prev => prev + 1);
        } else {
          setPosts(res.data);
          setPage(1);
        }
        setHasMore(res.data.length === 15);
      } else {
        setError('Failed to load posts');
      }
    } catch (err) {
      setError('Failed to load posts');
    }
    
    setLoading(false);
    setLoadingMore(false);
  };

  const loadMorePosts = () => {
    if (!loadingMore && hasMore) {
      loadPosts(true);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    setPage(0);
    setHasMore(true);
    await loadPosts();
    setRefreshing(false);
  };

  const handleCreatePost = () => {
    (navigation as any).navigate('newPost', { communityId });
  };

  if (loading) return <ActivityIndicator style={{ marginTop: 32 }} />;
  if (error) return <Text style={styles.error}>{error}</Text>;

  return (
    <View style={{ flex: 1 }}>
      {/* Create Post Button - Only visible to community members */}
      {isMember && (
        <TouchableOpacity style={styles.createPostButton} onPress={handleCreatePost}>
          <Text style={styles.createPostButtonText}>📝 Create Post</Text>
        </TouchableOpacity>
      )}

      {posts.length > 0 ? (
        <FlatList
          data={posts}
          renderItem={({ item }) => (
            <PostCardAny
              item={item}
              currentUser={user}
              allowLinks={Boolean(isLinkAllowed) && Boolean(authorAdminMap[String((item as any)?.userId || (item as any)?.user_id || (item as any)?.user?.id || '')])}
              showDelete={false}
              showReportSpam={false}
              onDelete={(post: any) => {
                // Handle post deletion
                setPosts(prev => prev.filter(p => p.id !== post.id));
              }}
              onEdit={(_post: any) => {
                // Handle post editing
              }}
            />
          )}
          keyExtractor={(item) => String((item as any).id)}
          contentContainerStyle={{ padding: 16 }}
          onEndReached={loadMorePosts}
          onEndReachedThreshold={0.5}
          ListFooterComponent={loadingMore ? <ActivityIndicator style={{ marginVertical: 10 }} /> : null}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[theme.colors.primary]}
              tintColor={theme.colors.primary}
            />
          }
        />
      ) : (
        <Text style={styles.empty}>No posts yet</Text>
      )}
    </View>
  );
};

const MembersTab = ({ communityId }: { communityId: string }) => {
  const { user } = useAuth();
  const navigation = useNavigation();
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false); // Start with false, will be updated after first load
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [lastResponseLength, setLastResponseLength] = useState(0); // Track last response length for hasMore calculation
  const retryCountRef = useRef(0);

  useEffect(() => {
    // Reset state when communityId changes
    setMembers([]);
    setHasMore(false); // Start with false until we load
    setTotalCount(null);
    setLastResponseLength(0);
    setError('');
    retryCountRef.current = 0;
    
    // Load both in parallel
    loadMembers();
    loadTotalCount();
  }, [communityId]);

  useEffect(() => {
    if (totalCount === null && members.length > 0 && !loading && !loadingMore && retryCountRef.current < 2) {
      const retryTimer = setTimeout(() => {
        retryCountRef.current += 1;
        loadTotalCount();
      }, 2000);
      
      return () => clearTimeout(retryTimer);
    }
  }, [totalCount, members.length, loading, loadingMore, communityId]);
  
  // Reset retry count when communityId changes
  useEffect(() => {
    retryCountRef.current = 0;
  }, [communityId]);

  useEffect(() => {
    if (members.length === 0) {
      setHasMore(false);
      return;
    }
    
    if (totalCount !== null && totalCount >= 0) {
      if (totalCount < members.length) {
      } else {
        const newHasMore = members.length < totalCount;
        setHasMore(newHasMore);
        return;
      }
    }
    
    if (lastResponseLength === 20) {
      setHasMore(true);
      return;
    }
    
    if (lastResponseLength > 0 && lastResponseLength < 20) {
      setHasMore(false);
      if (totalCount === null || totalCount < members.length) {
        setTotalCount(members.length);
      }
      return;
    }
    
    if (lastResponseLength === 0 && members.length > 0) {
      setHasMore(true);
    }
  }, [totalCount, members.length, lastResponseLength]);

  const loadTotalCount = async () => {
    try {
      // Use the community service function first (most reliable)
      const { getCommunityMemberCount } = await import('../../services/communityService');
      const countRes = await getCommunityMemberCount(communityId);
      
      if (countRes.success && countRes.data !== null && countRes.data !== undefined) {
        const count = countRes.data;
        setTotalCount(prevCount => {
          if (prevCount === null || count >= (prevCount || 0)) {
            return count;
          } else {
            return prevCount;
          }
        });
        return;
      }
      
      // Fallback: Get all user_communities and count them (this might fail due to RLS)
      const { data: allMembers, error: allMembersError, count } = await Supabase
        .from('user_communities')
        .select('user_id', { count: 'exact' })
        .eq('community_id', communityId);
      
      if (!allMembersError) {
        const memberCount = count !== null && count !== undefined ? count : (allMembers?.length || 0);
        if (memberCount >= 0) {
          setTotalCount(prevCount => {
            if (prevCount === null || memberCount >= (prevCount || 0)) {
              return memberCount;
            } else {
              return prevCount;
            }
          });
          return;
        }
      }
      
      // Final fallback: Direct count query (head only, doesn't return data)
      const { count: directCount, error: countError } = await Supabase
        .from('user_communities')
        .select('*', { count: 'exact', head: true })
        .eq('community_id', communityId);
      
      if (!countError && directCount !== null && directCount !== undefined) {
        setTotalCount(prevCount => {
          if (prevCount === null || directCount >= (prevCount || 0)) {
            return directCount;
          } else {
            return prevCount;
          }
        });
      } else {
        // Keep existing value (if any); response-based logic will handle pagination.
      }
    } catch (err) {
      // Keep existing value (if any) on error.
    }
  };

  const loadMembers = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setMembers([]); // Clear existing members on fresh load
    }
    
    try {
      const limit = 20;
      const offset = isLoadMore ? members.length : 0;
      
      // Try the new function first
      let res = await getCommunityMembersWithFunction(communityId, limit, offset);
      
      if (!res.success) {
        // Try the fallback approach
        res = await getCommunityMembers(communityId, limit, offset);
      }
      
      if (res.success && res.data && Array.isArray(res.data)) {
        const responseLength = res.data.length;
        setLastResponseLength(responseLength);
        
        let updatedMembers: any[];
        
        if (isLoadMore) {
          const existingIds = new Set(members.map(member => member.user_id));
          const newMembers = res.data.filter(member => !existingIds.has(member.user_id));
          
          if (newMembers.length === 0) {
            setLastResponseLength(0);
            setHasMore(false);
            setTotalCount(prevTotal => {
              if (prevTotal === null || members.length > (prevTotal || 0)) {
                return members.length;
              } else {
                return prevTotal;
              }
            });
            setLoading(false);
            setLoadingMore(false);
            return;
          }
          
          updatedMembers = [...members, ...newMembers];
          // lastResponseLength already set above - we use the API response length to determine if there might be more
          // (not the newMembers.length, since duplicates don't mean there are no more members)
        } else {
          updatedMembers = res.data;
          // lastResponseLength already set above
        }
        
        // Update members
        setMembers(updatedMembers);
        if (responseLength < limit && (totalCount === null || totalCount < updatedMembers.length)) {
          setTotalCount(updatedMembers.length);
        }
      } else {
        setError('Failed to load members');
      }
    } catch (err) {
      setError('Failed to load members');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreMembers = () => {
    if (!loadingMore && hasMore && members.length > 0) {
      loadMembers(true);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    setMembers([]);
    setHasMore(false);
    setTotalCount(null);
    setLastResponseLength(0);
    retryCountRef.current = 0;
    await Promise.all([loadMembers(), loadTotalCount()]);
    setRefreshing(false);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'creator':
        return '👑';
      case 'admin':
        return '⚡';
      case 'moderator':
        return '🛡️';
      default:
        return '✅';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'creator':
        return '#FFD700'; // Gold
      case 'admin':
        return '#FF6B35'; // Orange
      case 'moderator':
        return '#4CAF50'; // Green
      default:
        return '#2196F3'; // Blue
    }
  };

  const formatJoinDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) return <ActivityIndicator style={{ marginTop: 32 }} />;
  if (error) return <Text style={styles.error}>{error}</Text>;

  return (
    <View style={{ flex: 1 }}>
      
      {members.length > 0 ? (
        <FlatList
          data={members}
          renderItem={({ item }) => (
            <TouchableOpacity 
              style={styles.memberItem}
              onPress={() => {
                (navigation as any).navigate('profile', { userId: item.user_id });
              }}
            >
              <View style={styles.memberInfo}>
                <Avatar 
                  size={hp(5)}
                  uri={item.users?.image}
                  rounded={theme.radius.md}
                />
                <View style={styles.memberDetails}>
                  <Text style={styles.memberName}>{item.users?.name || 'Unknown User'}</Text>
                  <Text style={styles.memberJoinDate}>Joined {formatJoinDate(item.joined_at)}</Text>
                </View>
              </View>
              <View style={[styles.roleBadge, { backgroundColor: getRoleColor(item.role) }]}>
                <Text style={styles.roleText}>
                  {getRoleIcon(item.role)} {item.role.charAt(0).toUpperCase() + item.role.slice(1)}
                </Text>
              </View>
            </TouchableOpacity>
          )}
          keyExtractor={(item) => item.user_id}
          contentContainerStyle={{ padding: 16 }}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[theme.colors.primary]}
              tintColor={theme.colors.primary}
            />
          }
          ListFooterComponent={
            <>
              {hasMore ? (
                <View style={styles.loadMoreContainer}>
                  <TouchableOpacity 
                    onPress={() => {
                      if (!loadingMore && hasMore) {
                        loadMoreMembers();
                      }
                    }}
                    style={[styles.loadMoreButton, (loadingMore || refreshing) && styles.loadMoreButtonDisabled]}
                    disabled={loadingMore || refreshing}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.loadMoreText}>
                      {loadingMore ? 'Loading...' : 'Load More Members'}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : members.length > 0 ? (
                <View style={{ paddingVertical: 20, alignItems: 'center' }}>
                  <Text style={{ color: theme.colors.textLight, fontSize: 14 }}>
                    {totalCount !== null && members.length === totalCount 
                      ? `All ${totalCount} members loaded` 
                      : 'All members loaded'}
                  </Text>
                </View>
              ) : null}
            </>
          }
        />
      ) : (
        <Text style={styles.empty}>No members yet</Text>
      )}
    </View>
  );
};

const CommunityDetails = ({ route }: any) => {
  const { communityId, profileImage } = route.params;
  const [selected, setSelected] = useState('about');

  return (
    <ScreenWrapper bg="white">
      <Header title="Community Details" />
      <View style={{ flex: 1, backgroundColor: '#fff' }}>
        <View style={styles.tabBar}>
        {tabs.map(tab => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, selected === tab.key && styles.tabSelected]}
            onPress={() => setSelected(tab.key)}
          >
            <Text style={[styles.tabLabel, selected === tab.key && styles.tabLabelSelected]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
        {selected === 'about' && <AboutTab communityId={communityId} profileImage={profileImage} />}
        {selected === 'feed' && <PostsTab communityId={communityId} />}
        {selected === 'members' && <MembersTab communityId={communityId} />}
      </View>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#f2f2f2',
    padding: 6,
    borderRadius: 8,
    margin: 16,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    marginHorizontal: 2,
    borderRadius: 6,
    backgroundColor: '#eee',
  },
  tabSelected: {
    backgroundColor: theme.colors.primary,
  },
  tabLabel: {
    color: '#222',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  tabLabelSelected: {
    color: '#fff',
  },
  aboutTitle: { fontSize: 24, fontWeight: 'bold', marginBottom: 12, color: '#222' },
  aboutDesc: { fontSize: 16, color: '#555', marginBottom: 16 },
  aboutMeta: { fontSize: 14, color: '#888', marginBottom: 4 },
  error: { color: 'red', textAlign: 'center', marginTop: 32 },
  empty: { color: '#888', textAlign: 'center', marginTop: 32 },
  postCard: { backgroundColor: '#f2f2f2', borderRadius: 8, padding: 16, marginBottom: 12 },
  postBody: { fontSize: 16, color: '#222', marginBottom: 8 },
  postMeta: { fontSize: 12, color: '#555' },
  newPostButton: {
    backgroundColor: theme.colors.primaryDark,
    padding: 12,
    borderRadius: 8,
    margin: 16,
    alignItems: 'center',
  },
  newPostButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  newAnnouncementButton: {
    backgroundColor: theme.colors.primary,
    padding: 12,
    borderRadius: 8,
    margin: 16,
    alignItems: 'center',
  },
  newAnnouncementButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  detailsCommunityAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignSelf: 'center',
    marginBottom: 16,
    backgroundColor: '#ddd',
  },
  adminActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 16,
  },
  editButton: {
    backgroundColor: theme.colors.primaryDark,
    padding: 8,
    borderRadius: 6,
    alignItems: 'center',
    minWidth: 80,
  },
  editButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },

  announcementBadge: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  announcementBadgeText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    width: '80%',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    color: '#222',
  },
  input: {
    width: '100%',
    height: 50,
    borderColor: '#ccc',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    marginBottom: 15,
    fontSize: 16,
    color: '#222',
  },
  imagePicker: {
    backgroundColor: theme.colors.primary,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 15,
    width: '100%',
  },
  imagePickerText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  imagePreview: {
    width: 100,
    height: 100,
    borderRadius: 50,
    marginBottom: 15,
    backgroundColor: '#ddd',
  },
  createPostButton: {
    backgroundColor: theme.colors.primaryDark,
    padding: 12,
    borderRadius: 8,
    margin: 16,
    alignItems: 'center',
  },
  createPostButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  membersHeader: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#222',
    paddingHorizontal: 16,
  },
  memberItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: 4,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  memberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  memberDetails: {
    marginLeft: 12,
  },
  memberName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#222',
  },
  memberJoinDate: {
    fontSize: 12,
    color: '#555',
  },
  roleBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'center',
  },
  roleText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#fff',
  },
  loadMoreContainer: {
    marginVertical: 30,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  loadMoreButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: wp(8),
    paddingVertical: hp(1.5),
    borderRadius: theme.radius.lg || 8,
    minWidth: wp(40),
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreText: {
    color: 'white',
    fontSize: hp(1.8),
    fontWeight: '600',
    fontFamily: theme.fonts?.semibold,
  },
  loadMoreButtonDisabled: {
    opacity: 0.6,
  },
});

export default CommunityDetails; 